import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { SearchIcon } from "../../../assets/icons/interface/interface";
import Drawer from "../../../components/Drawer/Drawer";
import Icon from "../../../components/Icon/index.js";
import { Button } from "../../../components/ui/button.jsx";
import { JobApplicationState, JobApplicationStatus } from "../../../handlers/enums";
import useIsLoggedIn from "../../../hooks/use-IsLoggedIn";
import { useApplications, useUpdateApplicationStatus } from "../../../services/create-resume/queries";
import { Input } from "../../JobsPage/components/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../JobsPage/components/Select";
import Header from "../../Profile/components/Header";
import RecommendedJobs from "../../Profile/components/RecommendedJobs";
import SavedJobs from "../../Profile/components/SavedJobs";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../Profile/components/Tabs";
import Loader from "../../StaticPages/HomePage/components/Loader";
import ViewJob from "../../StaticPages/HomePage/components/ViewJob";
import ApplicationDetails from "./Components/ApplicationDetails";
import ApplicationRegModal from "./Components/ApplicationRegModal.jsx";
import SummarySection from "./Components/ApplicationSummary";
import ApplicationsTable from "./Components/ApplicationsTable";

function JobApplications() {
	// const PAGE_SIZE = 20;
	// const [page, setPage] = useState(1);
	const isLoggedIn = useIsLoggedIn();
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedJob, setSelectedJob] = useState(null);
	const [selectedJobs, setSelectedJobs] = useState([]);
	const [statusFilter, setStatusFilter] = useState("");
	const [isScrolled, setIsScrolled] = useState(false);
	const { data: applications, isLoading, refetch } = useApplications();
	const [selectedApplication, setSelectedApplication] = useState(null);
	const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
	const [IsDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
	const [showApplicationModal, setShowApplicationModal] = useState(false);
	// const [showPreferencesModal, setShowPreferencesModal] = useState(false);

	const updateApplicationStatusMutation = useUpdateApplicationStatus({
		onSuccess: () => {
			toast.success("Application Status updated successfully");
		},
		onError: (error) => {
			toast.error(error.response.data.message);
		},
	});

	// Handle status change
	const handleStatusChange = async (applicationId, newStatus, currentStatus) => {
		if (!currentStatus || newStatus !== currentStatus) {
			await updateApplicationStatusMutation
				.mutateAsync({
					applicationId,
					payload: {
						notes: null,
						dueDate: null,
						status: newStatus,
						state: JobApplicationState.NOT_STARTED,
					},
				})
				.catch(() => {});
		}
	};

	// Open drawer with job details
	const openJobDetails = (application) => {
		setSelectedApplication(application);
		setIsDetailsDrawerOpen(true);
	};

	// Handle filter change
	const handleFilterChange = (status) => {
		setStatusFilter(status);
	};

	// Status color mapping
	const getStatusColor = (status) => {
		const colors = {
			[JobApplicationStatus.APPLIED]: "bg-blue-100 text-blue-800",
			[JobApplicationStatus.SCREENING]: "bg-purple-100 text-purple-800",
			[JobApplicationStatus.REJECTED]: "bg-red-100 text-red-800",
			[JobApplicationStatus.RECRUITER_CALL]: "bg-yellow-100 text-yellow-800",
			[JobApplicationStatus.INTERVIEW]: "bg-green-100 text-green-800",
			[JobApplicationStatus.ORAL_INTERVIEW]: "bg-green-100 text-green-800",
			[JobApplicationStatus.TECHNICAL_INTERVIEW]: "bg-green-100 text-green-800",
			[JobApplicationStatus.TEAM_INTERVIEW]: "bg-green-100 text-green-800",
		};
		return colors[status] || "bg-gray-100 text-gray-800";
	};

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 0);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// Memoized filtered jobs to prevent unnecessary re-renders
	const [filteredJobs] = useMemo(() => {
		if (statusFilter === "ALL" && searchQuery === "") return [applications];

		let allJobs = applications;

		if (statusFilter) {
			allJobs =
				statusFilter === "ALL"
					? allJobs
					: allJobs.filter((job) => {
							const status = job.stages[job.stages.length - 1]?.status;
							return status === statusFilter;
						});
		}

		const filtered = searchQuery
			? allJobs.filter(
					(job) =>
						job.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
						job.jobTitle.toLowerCase().includes(searchQuery.toLowerCase()),
				)
			: allJobs;

		// if (selectedTab === "active") {
		// 	const activeJobs = filtered.filter(isJobActive);
		// 	return [activeJobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), activeJobs.length];
		// } else if (selectedTab === "closed") {
		// 	const closedJobs = filtered.filter(isJobClosed);
		// 	return [closedJobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), closedJobs.length];
		// } else if (selectedTab === "archived") {
		// 	const archivedJobs = filtered.filter(isJobArchived);
		// 	return [archivedJobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), archivedJobs.length];
		// }
		//
		// const pageJobs = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

		// console.log(filtered.length, "filtered jobs length");

		return [filtered];
	}, [applications, statusFilter, searchQuery]);

	useEffect(() => {}, [filteredJobs]);

	return (
		<>
			<ApplicationRegModal isOpen={showApplicationModal} onClose={() => setShowApplicationModal(false)} />

			<div className={`sticky top-0 z-50 bg-white transition-all duration-300 ${isScrolled ? "shadow-sm" : ""}`}>
				<Header isLoggedIn={isLoggedIn} variant="applicant" />
			</div>

			{/* Application details drawer */}
			<Drawer
				isOpen={IsDetailsDrawerOpen}
				onClose={() => {
					setIsDetailsDrawerOpen(false);
				}}
			>
				<ApplicationDetails
					application={selectedApplication}
					refetchData={() => refetch()}
					getStatusColor={getStatusColor}
				/>
			</Drawer>

			{/* Job details drawer */}
			<Drawer
				isOpen={showJobDetailsModal}
				onClose={() => {
					setShowJobDetailsModal(false);
				}}
			>
				<ViewJob data={selectedJob} setData={setSelectedJob} setShowJobDetailsModal={setShowJobDetailsModal} />
			</Drawer>

			<div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="flex justify-between items-center">
					<h2 className="text-3xl font-bold mb-2 text-gray-900 font-plus-jakarta">My Applications</h2>
					{/* Search and Filter */}
					<div className="flex gap-3">
						<div className="relative w-64">
							<SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
							<Input
								placeholder="Search applications..."
								className="pl-9 h-9 bg-gray-50 border-0 text-gray-900 placeholder:text-gray-600"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
						</div>
						<Select value={statusFilter} onValueChange={setStatusFilter}>
							<SelectTrigger className="w-40 h-9 border border-gray-300 mr-0">
								<SelectValue placeholder="Filter by status" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="ALL">All Statuses</SelectItem>
								{Object.keys(JobApplicationStatus).map((status) => (
									<SelectItem key={status} value={status}>
										{status.replace(/_/g, " ")}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				<div className="space-y-4">
					{isLoading ? (
						<div className="mt-5">
							<Loader />
						</div>
					) : filteredJobs === undefined || filteredJobs.length === 0 ? (
						<div className="text-center my-12 p-6 bg-gray-50 rounded-sm">
							<h3 className="text-xl font-medium text-gray-600">No Applications found</h3>
							<p className="mt-2 text-gray-500">
								{searchQuery
									? `No applications matching "${searchQuery}" found. Try a different search term.`
									: statusFilter
										? `No applications with status "${statusFilter}" found. Try a different filter.`
										: "You haven't Applied any jobs yet."}
							</p>
						</div>
					) : (
						<>
							{/* Summary Section */}
							<SummarySection
								applications={filteredJobs}
								statusFilter={statusFilter}
								handleFilterChange={handleFilterChange}
							/>

							<div className="flex justify-end" style={{ marginTop: 0 }} onClick={() => setShowApplicationModal(true)}>
								<Button variant="ghost" className="text-[#0B6BCF] font-medium pr-2">
									<Icon iconName="union-plus" className="mt-1 mr-2" size={12} /> Register
								</Button>
							</div>

							<ApplicationsTable
								jobs={filteredJobs}
								handleStatusChange={handleStatusChange}
								openJobDetails={openJobDetails}
								getStatusColor={getStatusColor}
								selectedJobs={selectedJobs}
								setSelectedJobs={setSelectedJobs}
							/>
							{/*<TablePagination*/}
							{/*	currentPage={page}*/}
							{/*	totalPages={Math.ceil(size / PAGE_SIZE)}*/}
							{/*	handleNext={() => setPage((prev) => Math.min(prev + 1, Math.ceil(size / PAGE_SIZE)))}*/}
							{/*	handlePrevious={() => setPage((prev) => Math.max(prev - 1, 1))}*/}
							{/*/>*/}

							{/* Job Recommendations Section */}
							<section id="job-recommendations" className="scroll-mt-24 mt-12">
								<div className="mb-4">
									<h2 className="text-xl font-bold text-gray-800 font-plus-jakarta">Job picks for you</h2>
								</div>

								<Tabs defaultValue="recommended" className="w-full">
									<TabsList className="flex border-b-0">
										<TabsTrigger
											value="recommended"
											className="ignore-focus-behavior rounded-none text-sm text-gray-700 mr-6"
										>
											Recommended
										</TabsTrigger>
										<TabsTrigger value="saved" className="ignore-focus-behavior rounded-none text-sm text-gray-700">
											Saved Jobs
										</TabsTrigger>
									</TabsList>

									<TabsContent value="recommended">
										<RecommendedJobs
											// setShowPreferencesModal={setShowPreferencesModal}
											setSelectedJob={setSelectedJob}
											setShowJobDetailsModal={setShowJobDetailsModal}
										/>
									</TabsContent>

									<TabsContent value="saved">
										<SavedJobs setSelectedJob={setSelectedJob} setShowJobDetailsModal={setShowJobDetailsModal} />
									</TabsContent>
								</Tabs>
							</section>
						</>
					)}
				</div>
			</div>
		</>
	);
}

export default JobApplications;
